// What the OMR reader read off each marked page of a split run: the page with
// every row's box drawn on it, and the rows as read — blank and spoiled
// included, which no answer entry carries. The debugging view for a sheet
// that marked a student wrong. See
// docs/FeatureDesigns/assignments-tests/omr-answer-sheet-target.md §4.
import { useState } from 'react';
import { Badge } from '@shared-ui/components';
import type { OmrSheetRowView, OmrSheetView } from './solutionSplitterTypes';

/** The answer step's own bar for a clear mark (ANSWER_SHEET_CLEAR_CONFIDENCE). */
const CLEAR = 0.8;

const STATUS_TEXT: Record<OmrSheetView['status'], string> = {
    read: 'read',
    not_aligned: 'not aligned — the four corner marks were not found',
    unreadable_image: 'the image could not be read',
    no_design: 'no sheet design for this paper or tenant',
    unavailable: 'the OMR reader did not answer',
};

const STATE_COLOR: Record<OmrSheetRowView['state'], string> = {
    answered: '#2563eb',
    blank: '#9ca3af',
    spoiled: '#dc2626',
};

function rowColor(r: OmrSheetRowView): string {
    return r.state === 'answered' && r.confidence < CLEAR ? '#d97706' : STATE_COLOR[r.state];
}

function Sheet({ sheet }: { sheet: OmrSheetView }) {
    const [focus, setFocus] = useState<number | null>(null);
    const count = (s: OmrSheetRowView['state']) => sheet.rows.filter(r => r.state === s).length;
    const faint = sheet.rows.filter(r => r.state === 'answered' && r.confidence < CLEAR).length;

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                <strong>Page {sheet.pageIndex + 1}</strong>
                <Badge variant={sheet.status === 'read' ? 'success' : 'warning'} size="sm">{STATUS_TEXT[sheet.status]}</Badge>
                {sheet.sheetId && (
                    <span className="tag" title="The sheet design it was read against">
                        {sheet.designName ? `${sheet.designName} · ` : ''}{sheet.sheetId} v{sheet.version}
                    </span>
                )}
                {sheet.enrolment && <span className="tag" title="The bubbled enrolment number">enrolment {sheet.enrolment}</span>}
                {sheet.status === 'read' && (
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {count('answered')} answered · {count('blank')} blank · {count('spoiled')} spoiled
                        {faint > 0 && ` · ${faint} faint (below ${Math.round(CLEAR * 100)}%)`}
                    </span>
                )}
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {sheet.pageUrl && (
                    <div style={{ position: 'relative', flex: '1 1 320px', maxWidth: 520 }}>
                        <img src={sheet.pageUrl} alt={`Page ${sheet.pageIndex + 1}`} style={{ width: '100%', display: 'block', border: '1px solid var(--border-color)' }} />
                        {sheet.rows.filter(r => r.box2d).map(r => {
                            const [y0, x0, y1, x1] = r.box2d!;
                            const on = focus === r.row;
                            return (
                                <div
                                    key={r.row}
                                    title={`Row ${r.row}: ${r.value || r.state}`}
                                    onMouseEnter={() => setFocus(r.row)}
                                    onMouseLeave={() => setFocus(null)}
                                    style={{
                                        position: 'absolute',
                                        top: `${y0 / 10}%`, left: `${x0 / 10}%`,
                                        height: `${(y1 - y0) / 10}%`, width: `${(x1 - x0) / 10}%`,
                                        border: `${on ? 3 : 1.5}px solid ${rowColor(r)}`,
                                        background: on ? `${rowColor(r)}33` : 'transparent',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            );
                        })}
                    </div>
                )}

                {sheet.rows.length > 0 && (
                    <table className="data-table" style={{ flex: '1 1 280px', fontSize: 13 }}>
                        <thead>
                            <tr><th>Row</th><th>Q</th><th>Read</th><th>State</th><th>Confidence</th></tr>
                        </thead>
                        <tbody>
                            {sheet.rows.map(r => (
                                <tr
                                    key={r.row}
                                    onMouseEnter={() => setFocus(r.row)}
                                    onMouseLeave={() => setFocus(null)}
                                    style={{ background: focus === r.row ? 'var(--bg-secondary)' : undefined }}
                                >
                                    <td>{r.row}</td>
                                    <td>Q{r.questionIndex + 1}</td>
                                    <td><strong>{r.value || '—'}</strong>{r.kind !== 'single' && <span className="muted"> ({r.kind})</span>}</td>
                                    <td style={{ color: STATE_COLOR[r.state] }}>{r.state}</td>
                                    <td style={{ color: r.confidence < CLEAR ? '#d97706' : undefined }}>{Math.round(r.confidence * 100)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export function OmrSheetReading({ sheets }: { sheets: OmrSheetView[] }) {
    return (
        <div className="card">
            <h3>OMR sheet ({sheets.length} page{sheets.length === 1 ? '' : 's'})</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
                Read by the OMR reader against the paper&apos;s sheet design — no model. Boxes: blue answered,
                amber faint, red spoiled, grey blank. Only rows the paper reaches are shown.
            </p>
            {sheets.map(s => <Sheet key={s.pageIndex} sheet={s} />)}
        </div>
    );
}
